class LightboxNavigationHandler {
  private lightboxContainer: HTMLElement | null = null;
  private isOpen: boolean = false;
  private currentUrl: string = "";
  private originalBodyClasses: string = "";
  private outroButton: HTMLElement | null = null;
  private outroWrapper: HTMLElement | null = null;
  private mouseX: number = 0;
  private mouseY: number = 0;
  private isHoveringOutro: boolean = false;
  private outroObserver: IntersectionObserver | null = null;
  private fluidContainer: HTMLElement | null = null;
  private interfaceCanvasContainer: HTMLElement | null = null;
  private categoryContainer: HTMLElement | null = null;
  private allowObserverPause: boolean = true;
  private requireHoldToClose: boolean = false; // Set to true to require holding button for 0.5s

  constructor() {
    // Store original body classes from homepage
    this.originalBodyClasses = document.body.className;

    // If we're starting on a single page, we need to fetch the homepage classes
    if (document.body.classList.contains("single")) {
      this.fetchHomepageClasses();

      // Prevent observer from pausing during initial load period
      // Wait for images to load and simulation to complete
      this.allowObserverPause = false;
      window.addEventListener(
        "fluidCanvasImagesLoaded",
        () => {
          // Give brief delay to ensure canvas pause has happened
          setTimeout(() => {
            this.allowObserverPause = true;
            console.log("Observer pause now allowed after images loaded");
          }, 100);
        },
        { once: true }
      );
    }

    this.init();
    this.initOutroButton();
    this.initOutroObserver();
  }

  private async fetchHomepageClasses(): Promise<void> {
    try {
      const homeUrl = window.location.origin + "/";
      const response = await fetch(homeUrl);
      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Store the actual homepage body classes
      this.originalBodyClasses = doc.body.className;
    } catch (error) {
      console.error("Failed to fetch homepage classes:", error);
      // Fallback to removing single-page specific classes
      this.originalBodyClasses = document.body.className
        .split(" ")
        .filter(
          (cls) =>
            !cls.startsWith("single") &&
            !cls.startsWith("postid-") &&
            !cls.startsWith("post-type-")
        )
        .join(" ");
    }
  }

  private init(): void {
    // Create lightbox container if it doesn't exist
    this.createLightboxContainer();

    // Intercept clicks on internal links
    document.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      const link = target.closest("a");

      // Only handle lightbox navigation if we're on a fluid page (index or single)
      const isFluidPage = document.body.hasAttribute("data-fluid");

      if (
        link &&
        link.href &&
        isFluidPage &&
        this.isInternalLink(link.href) &&
        this.isArticleLink(link.href) &&
        !link.hasAttribute("data-no-lightbox")
      ) {
        e.preventDefault();
        this.navigate(link.href);
      }
    });

    // Handle browser back/forward buttons
    window.addEventListener("popstate", (e) => {
      if (e.state && e.state.lightbox) {
        this.loadContent(window.location.href, false);
      } else {
        this.close();
      }
    });

    // Check if we're on a single page on load
    if (document.body.classList.contains("single")) {
      this.currentUrl = window.location.href;
      this.isOpen = true;
      this.lightboxContainer?.classList.add("open");

      // Set initial history state so back button works
      window.history.replaceState({ lightbox: true }, "", this.currentUrl);
    }

    // Close on escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        this.close();
      }
    });
  }

  private createLightboxContainer(): void {
    // Check if item-wrapper exists (on single pages)
    const existingWrapper = document.querySelector(".item-wrapper");

    if (existingWrapper) {
      this.lightboxContainer = existingWrapper as HTMLElement;

      // Add lightbox-article class if not present
      if (!this.lightboxContainer.classList.contains("lightbox-article")) {
        this.lightboxContainer.classList.add("lightbox-article");
      }

      // Add close button if it doesn't exist
      if (!this.lightboxContainer.querySelector(".lightbox-close")) {
        if (document.body.classList.contains("page-template-page-about")) {
          // Don't add close button on about page
          return;
        }
        const closeButton = document.createElement("button");
        closeButton.className = "lightbox-close";
        closeButton.setAttribute("aria-label", "Close article");
        closeButton.innerHTML = "×";
        this.lightboxContainer.insertBefore(
          closeButton,
          this.lightboxContainer.firstChild
        );
      }
    } else {
      // Create new container for homepage
      this.lightboxContainer = document.createElement("div");
      this.lightboxContainer.className = "lightbox-article item-wrapper";
      document.body.appendChild(this.lightboxContainer);
    }

    // Attach handlers
    this.attachLightboxHandlers();
  }

  private isInternalLink(href: string): boolean {
    try {
      const url = new URL(href);
      return url.hostname === window.location.hostname;
    } catch {
      return false;
    }
  }

  private isArticleLink(href: string): boolean {
    try {
      const url = new URL(href);
      const path = url.pathname;

      // Check if it's not the homepage or root
      if (path === "/" || path === "") {
        return false;
      }

      // Exclude common WordPress paths that aren't articles
      const excludedPaths = [
        "/wp-admin",
        "/wp-login",
        "/wp-content",
        "/category/",
        "/tag/",
        "/author/",
        "/page/",
        "/feed",
        "/sitemap",
        "/index",
        "/index/",
        "/about/",
        "/about",
      ];

      for (const excluded of excludedPaths) {
        if (path.startsWith(excluded)) {
          return false;
        }
      }

      // Article URLs typically have a format like /article-slug/ or /2025/12/article-slug/
      // This is a single post, not an archive or system page
      return true;
    } catch {
      return false;
    }
  }

  private async navigate(url: string): Promise<void> {
    if (url === this.currentUrl) return;

    // Update browser history
    window.history.pushState({ lightbox: true }, "", url);

    await this.loadContent(url, true);
  }

  private async loadContent(
    url: string,
    animate: boolean = true
  ): Promise<void> {
    if (!this.lightboxContainer) return;

    try {
      // Fetch the page content
      const response = await fetch(url);
      const html = await response.text();

      // Parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Security check: Verify this is actually a single post page
      if (
        !doc.body.classList.contains("single") &&
        !doc.body.classList.contains("single-post")
      ) {
        console.warn(
          "Loaded page is not a single post, falling back to regular navigation"
        );
        window.location.href = url;
        return;
      }

      // Extract the complete item-wrapper with all its attributes and classes
      const newWrapper = doc.querySelector(".item-wrapper");

      if (newWrapper) {
        // Clone the entire wrapper element to preserve structure
        const clonedWrapper = newWrapper.cloneNode(true) as HTMLElement;

        // Ensure it has the lightbox classes
        clonedWrapper.classList.add("lightbox-article", "open");

        if (animate) {
          this.lightboxContainer.classList.add("transitioning");

          setTimeout(() => {
            // Replace the entire lightbox container content
            const parent = this.lightboxContainer!.parentElement;
            if (parent) {
              parent.replaceChild(clonedWrapper, this.lightboxContainer!);
              this.lightboxContainer = clonedWrapper;

              // Re-attach event handlers
              this.attachLightboxHandlers();

              // Initialize Plyr players
              this.initializePlyrPlayers();

              // Initialize Splide carousels
              this.initializeSplideCarousels();

              // Initialize Swiper carousels
              this.initializeSwiperCarousels();

              // Dispatch custom event for other handlers (like scroll-to-top)
              window.dispatchEvent(
                new CustomEvent("lightboxContentLoaded", {
                  detail: { container: this.lightboxContainer },
                })
              );
            }

            this.lightboxContainer!.classList.remove("transitioning");
            this.lightboxContainer!.scrollTop = 0;
          }, 200);
        } else {
          const parent = this.lightboxContainer.parentElement;
          if (parent) {
            parent.replaceChild(clonedWrapper, this.lightboxContainer);
            this.lightboxContainer = clonedWrapper;

            // Re-attach event handlers
            this.attachLightboxHandlers();

            // Initialize Plyr players
            this.initializePlyrPlayers();

            // Initialize Splide carousels
            this.initializeSplideCarousels();

            // Initialize Swiper carousels
            this.initializeSwiperCarousels();

            // Dispatch custom event for other handlers (like scroll-to-top)
            window.dispatchEvent(
              new CustomEvent("lightboxContentLoaded", {
                detail: { container: this.lightboxContainer },
              })
            );
          }

          this.lightboxContainer.scrollTop = 0;
        }

        // Update document title
        const title = doc.querySelector("title");
        if (title) {
          document.title = title.textContent || "";
        }

        // Update body classes to match the loaded page
        document.body.className = doc.body.className;
        document.body.classList.add("lightbox-open");

        this.currentUrl = url;
        this.isOpen = true;
      } else {
        // No item-wrapper found, not a proper single page
        console.warn(
          "No item-wrapper found, falling back to regular navigation"
        );
        window.location.href = url;
      }
    } catch (error) {
      console.error("Failed to load content:", error);
      // Fallback to regular navigation
      window.location.href = url;
    }
  }

  private initOutroButton(): void {
    // Track mouse position
    document.addEventListener("mousemove", (e) => {
      this.mouseX = e.clientX;
      this.mouseY = e.clientY;

      // Check if hovering over outro-wrapper
      if (this.outroWrapper) {
        const rect = this.outroWrapper.getBoundingClientRect();
        this.isHoveringOutro =
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom;
      }

      this.updateOutroButtonPosition();
    });

    // Update position on scroll
    if (this.lightboxContainer) {
      this.lightboxContainer.addEventListener("scroll", () => {
        this.updateOutroButtonPosition();
      });
    }

    // Update outro button reference when content loads
    window.addEventListener("lightboxContentLoaded", () => {
      this.findOutroButton();
    });

    // Initial setup if button exists
    this.findOutroButton();
  }

  private initOutroObserver(): void {
    // Get references to canvas containers
    this.fluidContainer = document.querySelector(".fluid-container");
    this.interfaceCanvasContainer = document.querySelector(
      ".interface-canvas-container"
    );
    this.categoryContainer = document.querySelector(".category-container");

    // Create intersection observer for outro-wrapper
    this.outroObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Outro wrapper is in view - show canvas and unpause simulation
            this.showCanvasElements();
            this.unpauseSimulation();
          } else {
            // Outro wrapper is not in view - hide canvas and pause simulation
            this.hideCanvasElements();
            this.pauseSimulation();
          }
        });
      },
      {
        threshold: 0.01, // Trigger as soon as any part is visible
        rootMargin: "0px",
      }
    );

    // Observe outro-wrapper when lightbox content loads
    window.addEventListener("lightboxContentLoaded", () => {
      this.observeOutroWrapper();
    });

    // Initial observation if outro-wrapper already exists
    this.observeOutroWrapper();
  }

  private observeOutroWrapper(): void {
    // Find outro-wrapper
    const outroWrapper = document.querySelector(".outro-wrapper");

    if (outroWrapper && this.outroObserver) {
      // Stop observing previous wrapper if any
      this.outroObserver.disconnect();

      // Start observing new wrapper
      this.outroObserver.observe(outroWrapper);
      console.log("Observing outro-wrapper for canvas visibility");
    }
  }

  private showCanvasElements(): void {
    if (this.fluidContainer) {
      this.fluidContainer.style.visibility = "visible";
      this.fluidContainer.style.opacity = "1";
    }
    if (this.interfaceCanvasContainer) {
      this.interfaceCanvasContainer.style.visibility = "visible";
      this.interfaceCanvasContainer.style.opacity = "1";
    }
    if (this.categoryContainer) {
      this.categoryContainer.style.visibility = "visible";
      this.categoryContainer.style.opacity = "1";
    }
    console.log("Canvas elements shown");
  }

  private hideCanvasElements(): void {
    // Only hide if we're on a single page
    if (document.body.classList.contains("single")) {
      if (this.fluidContainer) {
        this.fluidContainer.style.visibility = "hidden";
        this.fluidContainer.style.opacity = "0";
      }
      if (this.interfaceCanvasContainer) {
        this.interfaceCanvasContainer.style.visibility = "hidden";
        this.interfaceCanvasContainer.style.opacity = "0";
      }
      if (this.categoryContainer) {
        this.categoryContainer.style.visibility = "hidden";
        this.categoryContainer.style.opacity = "0";
      }
      console.log("Canvas elements hidden");
    }
  }
  pauseSimulation(): void {
    // Disabled to avoid issues with Complianz plugin blocking initial render
    // The simulation will keep running in the background
    return;
  }

  private unpauseSimulation(): void {
    // Dispatch event to unpause the fluid canvas simulation
    window.dispatchEvent(new CustomEvent("unpauseFluidCanvas"));
  }

  private;

  private findOutroButton(): void {
    this.outroButton = document.querySelector(".outro-button");
    this.outroWrapper = document.querySelector(".outro-wrapper");

    if (this.outroButton) {
      if (this.requireHoldToClose) {
        // Hold-to-close behavior
        let holdTimer: ReturnType<typeof setTimeout> | null = null;
        let isHolding = false;

        const startHold = (e: MouseEvent | TouchEvent) => {
          e.preventDefault();
          e.stopPropagation();

          isHolding = true;
          this.outroButton?.classList.add("opening");

          // Start timer for 0.5 seconds
          holdTimer = setTimeout(() => {
            if (isHolding) {
              this.close();
            }
          }, 500);
        };

        const endHold = (e: MouseEvent | TouchEvent) => {
          e.preventDefault();
          e.stopPropagation();

          isHolding = false;
          this.outroButton?.classList.remove("opening");

          // Clear timer if released early
          if (holdTimer) {
            clearTimeout(holdTimer);
            holdTimer = null;
          }
        };

        // Add mouse events
        this.outroButton.addEventListener("mousedown", startHold);
        this.outroButton.addEventListener("mouseup", endHold);
        this.outroButton.addEventListener("mouseleave", endHold);

        // Add touch events for mobile
        this.outroButton.addEventListener("touchstart", startHold);
        this.outroButton.addEventListener("touchend", endHold);
        this.outroButton.addEventListener("touchcancel", endHold);

        // Prevent click from triggering - only hold should work
        this.outroButton.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
      } else {
        // Simple click behavior
        this.outroButton.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.close();
        });
      }

      // Initial position
      this.updateOutroButtonPosition();
    }
  }

  private updateOutroButtonPosition(): void {
    if (!this.outroButton || !this.outroWrapper) return;

    // Check if mobile (screen width <= 480px)
    const isMobile = window.innerWidth <= 480;

    const wrapperRect = this.outroWrapper.getBoundingClientRect();
    const scrollTop = this.lightboxContainer?.scrollTop || 0;

    // On mobile, always center the button
    if (isMobile || !this.isHoveringOutro) {
      // Center in outro-wrapper (with transition)
      this.outroButton.classList.add("centered");

      // Position relative to outro-wrapper
      const centerX = wrapperRect.width / 2;
      const centerY = wrapperRect.height / 2;

      this.outroButton.style.left = `${centerX}px`;
      this.outroButton.style.top = `${centerY}px`;
      this.outroButton.style.transform = `translate(-50%, -50%)`;
    } else {
      // Follow mouse when hovering over outro-wrapper on desktop (no transition)
      this.outroButton.classList.remove("centered");

      // Calculate position relative to outro-wrapper
      const relativeX = this.mouseX - wrapperRect.left;
      const relativeY = this.mouseY - wrapperRect.top;

      this.outroButton.style.left = `${relativeX}px`;
      this.outroButton.style.top = `${relativeY}px`;
      this.outroButton.style.transform = `translate(-50%, -50%)`;
    }
  }

  private attachLightboxHandlers(): void {
    if (!this.lightboxContainer) return;

    // Add close button handler
    const closeButton = this.lightboxContainer.querySelector(".lightbox-close");

    if (closeButton) {
      closeButton.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      });
    }

    // Close on background click
    this.lightboxContainer.addEventListener("click", (e) => {
      if (e.target === this.lightboxContainer) {
        this.close();
      }
    });

    // Find and setup outro button
    this.findOutroButton();
  }

  private initializePlyrPlayers(): void {
    if (!this.lightboxContainer) return;

    // Check if Plyr is available
    if (typeof (window as any).Plyr === "undefined") {
      console.warn("Plyr library not loaded");
      return;
    }

    const Plyr = (window as any).Plyr;

    // Initialize audio players
    const audioPlayers = this.lightboxContainer.querySelectorAll(
      "audio[id^='player-']"
    );
    audioPlayers.forEach((audio) => {
      const hasAutoplay = audio.hasAttribute("autoplay");
      const controls = ["play", "progress", "current-time", "mute", "volume"];

      // Add restart control if autoplay is enabled
      if (hasAutoplay) {
        controls.unshift("restart");
      }

      new Plyr(audio, {
        controls,
        autoplay: hasAutoplay,
      });
    });

    // Initialize video players
    const videoPlayers = this.lightboxContainer.querySelectorAll(
      "video[id^='video-']"
    );
    videoPlayers.forEach((video) => {
      const hasAutoplay = video.hasAttribute("autoplay");
      const controls = [
        "play",
        "progress",
        "current-time",
        "mute",
        "volume",
        "fullscreen",
      ];

      // Add restart control if autoplay is enabled
      if (hasAutoplay) {
        controls.unshift("restart");
      }

      new Plyr(video, {
        controls,
        autoplay: hasAutoplay,
      });
    });

    // Initialize embed players
    const embedPlayers = this.lightboxContainer.querySelectorAll(
      ".plyr__video-embed[id^='player-']"
    );
    embedPlayers.forEach((embed) => {
      const hasAutoplay =
        embed.hasAttribute("data-plyr-config") &&
        embed.getAttribute("data-plyr-config")?.includes("autoplay");
      const controls = [
        "play",
        "progress",
        "current-time",
        "mute",
        "volume",
        "fullscreen",
      ];

      // Add restart control if autoplay is enabled
      if (hasAutoplay) {
        controls.unshift("restart");
      }

      new Plyr(embed, {
        controls,
        autoplay: hasAutoplay,
      });
    });

    console.log("Plyr players initialized in lightbox");
  }

  private initializeSplideCarousels(): void {
    if (!this.lightboxContainer) return;

    // Check if Splide is available
    if (typeof (window as any).Splide === "undefined") {
      console.warn("Splide library not loaded");
      return;
    }

    const Splide = (window as any).Splide;

    // Find all splide carousels
    const carousels = this.lightboxContainer.querySelectorAll(
      ".splide[id^='splide-']"
    );

    carousels.forEach((carousel) => {
      // Skip if already initialized
      if (carousel.classList.contains("is-initialized")) {
        return;
      }

      new Splide(carousel, {
        type: "slide",
        rewind: false,
        perMove: 1,
        omitEnd: true,
        autoWidth: true,
        focus: 0,
      }).mount();
    });

    console.log("Splide carousels initialized in lightbox");
  }

  private initializeSwiperCarousels(): void {
    if (!this.lightboxContainer) return;

    // Check if Swiper is available
    if (typeof (window as any).Swiper === "undefined") {
      console.warn("Swiper library not loaded");
      return;
    }

    const Swiper = (window as any).Swiper;

    // Find all swiper carousels that haven't been initialized
    const carousels = this.lightboxContainer.querySelectorAll(
      ".swiper[id^='swiper-']"
    );

    carousels.forEach((carousel) => {
      // Skip if already initialized
      if (carousel.classList.contains("swiper-initialized")) {
        return;
      }

      // Get the pagination, prev, and next buttons within this carousel
      const carouselContainer = carousel.closest(".row-carousell");
      if (!carouselContainer) return;

      const pagination = carouselContainer.querySelector(".swiper-pagination");
      const nextButton = carouselContainer.querySelector(".swiper-button-next");
      const prevButton = carouselContainer.querySelector(".swiper-button-prev");

      new Swiper(carousel as HTMLElement, {
        pagination: {
          el: pagination as HTMLElement,
          type: "fraction",
        },
        navigation: {
          nextEl: nextButton as HTMLElement,
          prevEl: prevButton as HTMLElement,
        },
        slidesPerView: "auto",
      });

      console.log(`Initialized swiper ${carousel.id}`);
    });

    console.log("Swiper carousels initialized in lightbox");
  }

  private open(): void {
    if (!this.lightboxContainer || this.isOpen) return;

    this.isOpen = true;
    this.lightboxContainer.classList.add("open");
    document.body.classList.add("lightbox-open");

    console.log("Lightbox opened");
  }

  public close(): void {
    if (!this.lightboxContainer || !this.isOpen) return;

    this.isOpen = false;
    this.lightboxContainer.classList.remove("open");

    // Clear current URL so the same article can be clicked again
    this.currentUrl = "";

    // Restore original homepage body classes
    document.body.className = this.originalBodyClasses;

    // Remove inline visibility styles to restore CSS defaults
    if (this.fluidContainer) {
      this.fluidContainer.style.visibility = "";
      this.fluidContainer.style.opacity = "";
    }
    if (this.interfaceCanvasContainer) {
      this.interfaceCanvasContainer.style.visibility = "";
      this.interfaceCanvasContainer.style.opacity = "";
    }
    if (this.categoryContainer) {
      this.categoryContainer.style.visibility = "";
      this.categoryContainer.style.opacity = "";
    }

    // Update URL to homepage
    const homeUrl = window.location.origin + "/";
    if (window.location.href !== homeUrl) {
      window.history.pushState({ lightbox: false }, "", homeUrl);
    }

    console.log("Lightbox closed");
  }

  public isLightboxOpen(): boolean {
    return this.isOpen;
  }
}

export default LightboxNavigationHandler;
